import React from 'react';
import styled from 'styled-components';
import { Body7 } from '@ui';
import { TransparentFlatButton } from '@ui/buttons';
import { EditableCell } from './EditableCell';
import { NestedCell } from './NestedCell';

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const StyledTable = styled.table`
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder || '#e0e0e0'};
    vertical-align: top;
  }

  th {
    position: relative;
    padding: 8px 12px;
    text-align: left;
    background-color: ${({ theme }) => theme.themeColors.componentNoOpacity || '#f8f8f8'};
    color: ${({ theme }) => theme.themeColors.text};
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  td {
    padding: 4px;
    overflow: hidden;
  }
`;

const ResizeHandle = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  cursor: col-resize;
  user-select: none;

  &:hover,
  &.active {
    background-color: ${({ theme }) => theme.themeColors.primaryColor || '#4a9eff'};
    opacity: 0.4;
  }
`;

const IndexLabel = styled(Body7)`
  color: ${({ theme }) => theme.themeColors.secondaryText || '#999'};
  min-width: 24px;
  text-align: right;
  padding: 4px 0 4px 8px;
  flex-shrink: 0;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: none;
  border-radius: 3px;
  background: transparent;
  color: ${({ theme }) => theme.themeColors.buttonText || '#999'};
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  flex-shrink: 0;
  transition: all 0.15s ease;
  opacity: 0;

  &:hover {
    background: ${({ theme }) => theme.themeColors.error || '#e74c3c'};
    color: ${({ theme }) => theme.themeColors.buttonText || '#fff'};
  }
`;

const PrimitiveRowWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder || '#e0e0e0'};
  }

  &:hover ${DeleteButton} {
    opacity: 1;
  }
`;

const RowWithDelete = styled.tr`
  &:hover ${DeleteButton} {
    opacity: 1;
  }
`;

const DeleteCell = styled.td`
  && {
    width: 30px;
    min-width: 30px;
    max-width: 30px;
    padding: 4px;
    text-align: center;
    border: none;
  }
`;

const DeleteHeaderCell = styled.th`
  && {
    width: 30px;
    min-width: 30px;
    max-width: 30px;
    border: none;
    background: transparent;
  }
`;

const ROW_CTRL_WIDTH = 32;

const RowCtrlHeaderCell = styled.th`
  && {
    width: ${ROW_CTRL_WIDTH}px;
    min-width: ${ROW_CTRL_WIDTH}px;
    max-width: ${ROW_CTRL_WIDTH}px;
    border: none;
    background: transparent;
    padding: 0;
  }
`;

const RowCtrlCell = styled.td`
  && {
    width: ${ROW_CTRL_WIDTH}px;
    min-width: ${ROW_CTRL_WIDTH}px;
    max-width: ${ROW_CTRL_WIDTH}px;
    padding: 4px 0;
    text-align: center;
    border: none;
  }
`;

const RowToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
    fill: ${({ theme }) => theme.themeColors.secondaryText || '#999'};
  }

  &:hover svg {
    fill: ${({ theme }) => theme.themeColors.text};
  }
`;

const SmallButtonWrapper = styled.div`
  margin-top: 6px;
  padding-bottom: 12px;

  button {
    padding: 4px 12px;
  }
`;

const TableToolbar = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 6px;
  margin-left: 32px;
`;

const ToolbarButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border: none;
  border-radius: 3px;
  background: transparent;
  cursor: pointer;
  font-size: 11px;
  color: ${({ theme }) => theme.themeColors.secondaryText || '#999'};

  svg {
    width: 14px;
    height: 14px;
    fill: ${({ theme }) => theme.themeColors.secondaryText || '#999'};
  }

  &:hover {
    color: ${({ theme }) => theme.themeColors.text};
    background: ${({ theme }) => theme.themeColors.primaryLowOpacity || 'rgba(74, 158, 255, 0.1)'};

    svg {
      fill: ${({ theme }) => theme.themeColors.text};
    }
  }
`;

const ExpandCollapseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  margin-left: 6px;
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
    fill: ${({ theme }) => theme.themeColors.secondaryText || '#999'};
  }

  &:hover svg {
    fill: ${({ theme }) => theme.themeColors.text};
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-right: 8px;
`;

const HideColumnButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  margin-left: 4px;
  flex-shrink: 0;
  opacity: 0;

  svg {
    width: 14px;
    height: 14px;
    fill: ${({ theme }) => theme.themeColors.secondaryText || '#999'};
  }

  &:hover svg {
    fill: ${({ theme }) => theme.themeColors.text};
  }
`;

const HeaderWrapper = styled.div`
  &:hover ${HideColumnButton} {
    opacity: 1;
  }
`;

const HiddenColumnsBar = styled.div`
  display: inline-flex;
  align-items: center;
  position: relative;
  margin-left: 8px;
`;

const HiddenColumnsButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder || '#ddd'};
  border-radius: 3px;
  background: transparent;
  cursor: pointer;
  font-size: 11px;
  color: ${({ theme }) => theme.themeColors.secondaryText || '#999'};

  &:hover {
    color: ${({ theme }) => theme.themeColors.text};
    background: ${({ theme }) => theme.themeColors.primaryLowOpacity || 'rgba(74, 158, 255, 0.1)'};
  }
`;

const HiddenColumnsMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: ${({ theme }) => theme.themeColors.component || '#fff'};
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder || '#ddd'};
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 160px;
  padding: 4px 0;
`;

const HiddenMenuItem = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 6px 12px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  color: ${({ theme }) => theme.themeColors.text};
  text-align: left;

  &:hover {
    background: ${({ theme }) => theme.themeColors.primaryLowOpacity || 'rgba(74, 158, 255, 0.1)'};
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.themeColors.lowContrastBorder || '#ddd'};
  margin: 4px 0;
`;

const DEFAULT_COL_WIDTH = 180;
const MIN_COL_WIDTH = 60;
const MAX_AUTO_COL_WIDTH = 600;
const CHAR_PX = 7.5;
const CELL_PADDING_PX = 28;
const SAMPLE_ROW_COUNT = 30;

const isPrimitive = (val) => val === null || typeof val !== 'object';

const estimateContentPx = (value, depth = 0) => {
  if (value == null) return 4 * CHAR_PX;
  if (typeof value === 'boolean') return (value ? 4 : 5) * CHAR_PX;
  if (typeof value === 'number') return String(value).length * CHAR_PX;
  if (typeof value === 'string') {
    if (value.length === 0) return 0;
    const lines = value.split('\n');
    return Math.max(...lines.map((l) => l.length)) * CHAR_PX;
  }
  if (depth > 4) return DEFAULT_COL_WIDTH;

  if (Array.isArray(value)) {
    if (value.length === 0) return DEFAULT_COL_WIDTH;
    if (value.every(isPrimitive)) {
      let maxW = 0;
      value.forEach((item) => {
        const w = estimateContentPx(item, depth + 1);
        if (w > maxW) maxW = w;
      });
      return maxW + 60;
    }
    const cols = getColumns(value);
    let total = 0;
    cols.forEach((col) => {
      let maxColW = col.length * CHAR_PX;
      value.slice(0, SAMPLE_ROW_COUNT).forEach((row) => {
        const w = estimateContentPx(row?.[col], depth + 1);
        if (w > maxColW) maxColW = w;
      });
      total += maxColW + CELL_PADDING_PX;
    });
    return total + 40;
  }

  if (typeof value === 'object') {
    const cols = Object.keys(value);
    let total = 0;
    cols.forEach((col) => {
      const headerW = col.length * CHAR_PX;
      const contentW = estimateContentPx(value[col], depth + 1);
      total += Math.max(headerW, contentW) + CELL_PADDING_PX;
    });
    return total;
  }

  return DEFAULT_COL_WIDTH;
};

const computeInitialColumnWidths = (data, columns) => {
  const rows = Array.isArray(data) ? data.slice(0, SAMPLE_ROW_COUNT) : [data];
  const widths = {};

  columns.forEach((col) => {
    let maxPx = col.length * CHAR_PX + CELL_PADDING_PX;

    rows.forEach((row) => {
      const px = estimateContentPx(row?.[col]) + CELL_PADDING_PX;
      if (px > maxPx) maxPx = px;
    });

    widths[col] = Math.min(MAX_AUTO_COL_WIDTH, Math.max(DEFAULT_COL_WIDTH, Math.round(maxPx)));
  });

  return widths;
};

export const getColumnsFromData = (data) => {
  if (Array.isArray(data)) {
    const keySet = new Set();
    data.forEach((item) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        Object.keys(item).forEach((k) => keySet.add(k));
      }
    });
    return Array.from(keySet);
  }
  if (data && typeof data === 'object') {
    return Object.keys(data);
  }
  return [];
};

const getColumns = (data) => {
  if (Array.isArray(data)) {
    const keySet = new Set();
    data.forEach((item) => {
      if (item && typeof item === 'object' && !Array.isArray(item)) {
        Object.keys(item).forEach((k) => keySet.add(k));
      }
    });
    return Array.from(keySet);
  }
  if (data && typeof data === 'object') {
    return Object.keys(data);
  }
  return [];
};

const getDefaultValueForColumn = (rows, column) => {
  for (const row of rows) {
    const val = row?.[column];
    if (val == null || typeof val !== 'object') continue;
    if (Array.isArray(val)) {
      if (val.length > 0 && val.some((item) => !isPrimitive(item))) {
        const innerKeys = new Set();
        val.forEach((item) => {
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            Object.keys(item).forEach((k) => innerKeys.add(k));
          }
        });
        return [Object.fromEntries([...innerKeys].map((k) => [k, '']))];
      }
      return [''];
    }
    const keys = Object.keys(val);
    return Object.fromEntries(keys.map((k) => [k, '']));
  }
  return '';
};

const isArrayOfPrimitives = (data) => {
  return Array.isArray(data) && data.length > 0 && data.every(isPrimitive);
};

const isArrayOfObjects = (data) => {
  return Array.isArray(data) && data.length > 0 && data.some((item) => !isPrimitive(item));
};

const ResizableHeader = ({ column, width, onResize, onAutoFit, isExpanded, onToggleExpand, onHide }) => {
  const handleRef = React.useRef(null);

  const handleMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = width;

    const handleMouseMove = (moveEvent) => {
      const newWidth = Math.max(MIN_COL_WIDTH, startWidth + (moveEvent.clientX - startX));
      onResize(column, newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      if (handleRef.current) handleRef.current.classList.remove('active');
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    if (handleRef.current) handleRef.current.classList.add('active');
  };

  const handleDoubleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onAutoFit(column);
  };

  return (
    <th style={{ width: `${width}px` }}>
      <HeaderWrapper>
        <HeaderContent>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Body7 bold>{column}</Body7>
            <HideColumnButton
              onClick={(e) => {
                e.stopPropagation();
                onHide(column);
              }}
              title="Hide column"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M11.83,9L15,12.16C15,12.11 15,12.05 15,12A3,3 0 0,0 12,9C11.94,9 11.89,9 11.83,9M7.53,9.8L9.08,11.35C9.03,11.56 9,11.77 9,12A3,3 0 0,0 12,15C12.22,15 12.44,14.97 12.65,14.92L14.2,16.47C13.53,16.8 12.79,17 12,17A5,5 0 0,1 7,12C7,11.21 7.2,10.47 7.53,9.8M2,4.27L4.28,6.55L4.73,7C3.08,8.3 1.78,10 1,12C2.73,16.39 7,19.5 12,19.5C13.55,19.5 15.03,19.2 16.38,18.66L16.81,19.08L19.73,22L21,20.73L3.27,3M12,7A5,5 0 0,1 17,12C17,12.64 16.87,13.26 16.64,13.82L19.57,16.75C21.07,15.5 22.27,13.86 23,12C21.27,7.61 17,4.5 12,4.5C10.6,4.5 9.26,4.75 8,5.2L10.17,7.35C10.74,7.13 11.35,7 12,7Z" />
              </svg>
            </HideColumnButton>
          </div>
          <ExpandCollapseButton
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand(column);
            }}
            title={isExpanded ? 'Collapse all' : 'Expand all'}
          >
            {isExpanded ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M4,2A2,2 0 0,0 2,4V14H4V4H14V2H4M8,6A2,2 0 0,0 6,8V18H8V8H18V6H8M20,12V20H12V12H20M20,10H12A2,2 0 0,0 10,12V20A2,2 0 0,0 12,22H20A2,2 0 0,0 22,20V12A2,2 0 0,0 20,10M19,17H13V15H19V17Z" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M18,8H8V18H6V8A2,2 0 0,1 8,6H18V8M14,2H4A2,2 0 0,0 2,4V14H4V4H14V2M22,12V20A2,2 0 0,1 20,22H12A2,2 0 0,1 10,20V12A2,2 0 0,1 12,10H20A2,2 0 0,1 22,12M20,15H17V12H15V15H12V17H15V20H17V17H20V15Z" />
              </svg>
            )}
          </ExpandCollapseButton>
        </HeaderContent>
      </HeaderWrapper>
      <ResizeHandle ref={handleRef} onMouseDown={handleMouseDown} onDoubleClick={handleDoubleClick} />
    </th>
  );
};

const HiddenColumnsDropdown = ({ hiddenColumns, onUnhide, onUnhideAll }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (hiddenColumns.length === 0) return null;

  return (
    <HiddenColumnsBar ref={ref}>
      <HiddenColumnsButton onClick={() => setOpen((p) => !p)}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14">
          <path
            d="M11.83,9L15,12.16C15,12.11 15,12.05 15,12A3,3 0 0,0 12,9C11.94,9 11.89,9 11.83,9M7.53,9.8L9.08,11.35C9.03,11.56 9,11.77 9,12A3,3 0 0,0 12,15C12.22,15 12.44,14.97 12.65,14.92L14.2,16.47C13.53,16.8 12.79,17 12,17A5,5 0 0,1 7,12C7,11.21 7.2,10.47 7.53,9.8M2,4.27L4.28,6.55L4.73,7C3.08,8.3 1.78,10 1,12C2.73,16.39 7,19.5 12,19.5C13.55,19.5 15.03,19.2 16.38,18.66L16.81,19.08L19.73,22L21,20.73L3.27,3M12,7A5,5 0 0,1 17,12C17,12.64 16.87,13.26 16.64,13.82L19.57,16.75C21.07,15.5 22.27,13.86 23,12C21.27,7.61 17,4.5 12,4.5C10.6,4.5 9.26,4.75 8,5.2L10.17,7.35C10.74,7.13 11.35,7 12,7Z"
            fill="currentColor"
          />
        </svg>
        Hiding {hiddenColumns.length} column{hiddenColumns.length !== 1 ? 's' : ''}
      </HiddenColumnsButton>
      {open && (
        <HiddenColumnsMenu>
          <HiddenMenuItem
            onClick={() => {
              onUnhideAll();
              setOpen(false);
            }}
          >
            Unhide all
          </HiddenMenuItem>
          <MenuDivider />
          {hiddenColumns.map((col) => (
            <HiddenMenuItem
              key={col}
              onClick={() => {
                onUnhide(col);
                setOpen(false);
              }}
            >
              {col}
            </HiddenMenuItem>
          ))}
        </HiddenColumnsMenu>
      )}
    </HiddenColumnsBar>
  );
};

const computeAutoFitWidth = (data, column) => {
  const rows = Array.isArray(data) ? data : [data];
  let maxPx = column.length * CHAR_PX + CELL_PADDING_PX;

  rows.forEach((row) => {
    const px = estimateContentPx(row?.[column]) + CELL_PADDING_PX;
    if (px > maxPx) maxPx = px;
  });

  return Math.max(MIN_COL_WIDTH, Math.round(maxPx));
};

const computeTableWidth = (columns, getWidth) => {
  return columns.reduce((sum, col) => sum + getWidth(col), 0);
};

export const JsonTable = ({ data, path = [], onUpdate, depth = 0 }) => {
  const derivedColumns = React.useMemo(() => getColumns(data), [data]);
  const columnsRef = React.useRef(null);
  const columns = React.useMemo(() => {
    if (columnsRef.current === null) {
      columnsRef.current = derivedColumns;
      return derivedColumns;
    }
    const currentSet = new Set(derivedColumns);
    const existingSet = new Set(columnsRef.current);
    const stable = columnsRef.current.filter((c) => currentSet.has(c));
    const added = derivedColumns.filter((c) => !existingSet.has(c));
    const result = [...stable, ...added];
    columnsRef.current = result;
    return result;
  }, [derivedColumns]);
  const autoWidths = React.useMemo(() => computeInitialColumnWidths(data, columns), [data, columns]);
  const [columnWidths, setColumnWidths] = React.useState({});
  const signalVersionRef = React.useRef(0);
  const nextVersion = React.useCallback(() => ++signalVersionRef.current, []);
  const [collapseSignal, setCollapseSignal] = React.useState({});
  const [rowCollapseSignal, setRowCollapseSignal] = React.useState({});
  const [hiddenColumns, setHiddenColumns] = React.useState(new Set());

  const visibleColumns = React.useMemo(() => columns.filter((c) => !hiddenColumns.has(c)), [columns, hiddenColumns]);
  const hiddenColumnsList = React.useMemo(() => columns.filter((c) => hiddenColumns.has(c)), [columns, hiddenColumns]);

  const handleHideColumn = React.useCallback((column) => {
    setHiddenColumns((prev) => new Set([...prev, column]));
  }, []);

  const handleUnhideColumn = React.useCallback((column) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      next.delete(column);
      return next;
    });
  }, []);

  const handleUnhideAll = React.useCallback(() => {
    setHiddenColumns(new Set());
  }, []);

  const handleResize = React.useCallback((column, newWidth) => {
    setColumnWidths((prev) => ({ ...prev, [column]: newWidth }));
  }, []);

  const handleAutoFit = React.useCallback(
    (column) => {
      const fitWidth = computeAutoFitWidth(data, column);
      setColumnWidths((prev) => ({ ...prev, [column]: fitWidth }));
    },
    [data]
  );

  const handleToggleExpand = React.useCallback(
    (column) => {
      const v = nextVersion();
      setCollapseSignal((prev) => {
        const cur = prev[column] || { collapsed: false, version: 0 };
        return { ...prev, [column]: { collapsed: !cur.collapsed, version: v } };
      });
    },
    [nextVersion]
  );

  const handleToggleRow = React.useCallback(
    (rowIndex) => {
      const v = nextVersion();
      setRowCollapseSignal((prev) => {
        const cur = prev[rowIndex] || { collapsed: false, version: 0 };
        return { ...prev, [rowIndex]: { collapsed: !cur.collapsed, version: v } };
      });
    },
    [nextVersion]
  );

  const handleExpandAll = React.useCallback(() => {
    const v = nextVersion();
    setCollapseSignal(() => {
      const next = {};
      columns.forEach((col) => {
        next[col] = { collapsed: false, version: v };
      });
      return next;
    });
    setRowCollapseSignal(() => {
      const next = {};
      if (Array.isArray(data)) {
        data.forEach((_, i) => {
          next[i] = { collapsed: false, version: v };
        });
      }
      return next;
    });
  }, [columns, data, nextVersion]);

  const handleCollapseAll = React.useCallback(() => {
    const v = nextVersion();
    setCollapseSignal(() => {
      const next = {};
      columns.forEach((col) => {
        next[col] = { collapsed: true, version: v };
      });
      return next;
    });
    setRowCollapseSignal(() => {
      const next = {};
      if (Array.isArray(data)) {
        data.forEach((_, i) => {
          next[i] = { collapsed: true, version: v };
        });
      }
      return next;
    });
  }, [columns, data, nextVersion]);

  const getWidth = (col) => columnWidths[col] || autoWidths[col] || DEFAULT_COL_WIDTH;

  const renderTable = (nestedData, nestedPath, nestedDepth) => (
    <JsonTable data={nestedData} path={nestedPath} onUpdate={onUpdate} depth={nestedDepth} />
  );

  const renderCell = (value, cellPath, cellSignal) => {
    if (isPrimitive(value)) {
      return (
        <EditableCell value={value} onUpdate={(newVal) => onUpdate(cellPath, newVal)} collapseSignal={cellSignal} />
      );
    }
    return (
      <NestedCell
        value={value}
        path={cellPath}
        onUpdate={onUpdate}
        depth={depth}
        renderTable={renderTable}
        collapseSignal={cellSignal}
      />
    );
  };

  if (isPrimitive(data)) {
    return <EditableCell value={data} onUpdate={(newVal) => onUpdate(path, newVal)} />;
  }

  if (isArrayOfPrimitives(data)) {
    const handleAddPrimitive = () => {
      onUpdate(path, [...data, '']);
    };
    const handleDeletePrimitive = (index) => {
      onUpdate(
        path,
        data.filter((_, i) => i !== index)
      );
    };
    return (
      <div>
        {data.map((item, index) => (
          <PrimitiveRowWrapper key={index}>
            <IndexLabel>{index + 1}</IndexLabel>
            <div style={{ flex: 1 }}>
              <EditableCell value={item} onUpdate={(newVal) => onUpdate([...path, index], newVal)} />
            </div>
            <DeleteButton onClick={() => handleDeletePrimitive(index)} title="Delete item">
              &times;
            </DeleteButton>
          </PrimitiveRowWrapper>
        ))}
        <SmallButtonWrapper>
          <TransparentFlatButton onClick={handleAddPrimitive}>+ Add item</TransparentFlatButton>
        </SmallButtonWrapper>
      </div>
    );
  }

  if (isArrayOfObjects(data)) {
    const tableWidth = computeTableWidth(visibleColumns, getWidth) + ROW_CTRL_WIDTH;
    const handleAddRow = () => {
      const emptyRow = columns.reduce((obj, col) => ({ ...obj, [col]: getDefaultValueForColumn(data, col) }), {});
      onUpdate(path, [...data, emptyRow]);
    };
    const handleDeleteRow = (rowIndex) => {
      onUpdate(
        path,
        data.filter((_, i) => i !== rowIndex)
      );
    };
    const getCellSignal = (col, rowIndex) => {
      const colSig = collapseSignal[col] || { collapsed: false, version: 0 };
      const rowSig = rowCollapseSignal[rowIndex] || { collapsed: false, version: 0 };
      return rowSig.version > colSig.version ? rowSig : colSig;
    };
    return (
      <TableWrapper>
        <TableToolbar>
          <ToolbarButton onClick={handleExpandAll} title="Expand all columns">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M18,8H8V18H6V8A2,2 0 0,1 8,6H18V8M14,2H4A2,2 0 0,0 2,4V14H4V4H14V2M22,12V20A2,2 0 0,1 20,22H12A2,2 0 0,1 10,20V12A2,2 0 0,1 12,10H20A2,2 0 0,1 22,12M20,15H17V12H15V15H12V17H15V20H17V17H20V15Z" />
            </svg>
            Expand All
          </ToolbarButton>
          <ToolbarButton onClick={handleCollapseAll} title="Collapse all columns">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M4,2A2,2 0 0,0 2,4V14H4V4H14V2H4M8,6A2,2 0 0,0 6,8V18H8V8H18V6H8M20,12V20H12V12H20M20,10H12A2,2 0 0,0 10,12V20A2,2 0 0,0 12,22H20A2,2 0 0,0 22,20V12A2,2 0 0,0 20,10M19,17H13V15H19V17Z" />
            </svg>
            Collapse All
          </ToolbarButton>
          <HiddenColumnsDropdown
            hiddenColumns={hiddenColumnsList}
            onUnhide={handleUnhideColumn}
            onUnhideAll={handleUnhideAll}
          />
        </TableToolbar>
        <StyledTable style={{ width: `${tableWidth}px`, minWidth: `${tableWidth}px` }}>
          <thead>
            <tr>
              <RowCtrlHeaderCell />
              {visibleColumns.map((col) => (
                <ResizableHeader
                  key={col}
                  column={col}
                  width={getWidth(col)}
                  onResize={handleResize}
                  onAutoFit={handleAutoFit}
                  isExpanded={!collapseSignal[col]?.collapsed}
                  onToggleExpand={handleToggleExpand}
                  onHide={handleHideColumn}
                />
              ))}
              <DeleteHeaderCell />
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => {
              const isRowExpanded = !rowCollapseSignal[rowIndex]?.collapsed;
              return (
                <RowWithDelete key={rowIndex}>
                  <RowCtrlCell>
                    <RowToggleButton
                      onClick={() => handleToggleRow(rowIndex)}
                      title={isRowExpanded ? 'Collapse row' : 'Expand row'}
                    >
                      {isRowExpanded ? (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                          <path d="M4,2A2,2 0 0,0 2,4V14H4V4H14V2H4M8,6A2,2 0 0,0 6,8V18H8V8H18V6H8M20,12V20H12V12H20M20,10H12A2,2 0 0,0 10,12V20A2,2 0 0,0 12,22H20A2,2 0 0,0 22,20V12A2,2 0 0,0 20,10M19,17H13V15H19V17Z" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                          <path d="M18,8H8V18H6V8A2,2 0 0,1 8,6H18V8M14,2H4A2,2 0 0,0 2,4V14H4V4H14V2M22,12V20A2,2 0 0,1 20,22H12A2,2 0 0,1 10,20V12A2,2 0 0,1 12,10H20A2,2 0 0,1 22,12M20,15H17V12H15V15H12V17H15V20H17V17H20V15Z" />
                        </svg>
                      )}
                    </RowToggleButton>
                  </RowCtrlCell>
                  {visibleColumns.map((col) => {
                    const cellValue = row?.[col];
                    const cellPath = [...path, rowIndex, col];
                    const cellSignal = getCellSignal(col, rowIndex);
                    return (
                      <td key={col}>{cellValue === undefined ? null : renderCell(cellValue, cellPath, cellSignal)}</td>
                    );
                  })}
                  <DeleteCell>
                    <DeleteButton onClick={() => handleDeleteRow(rowIndex)} title="Delete row">
                      &times;
                    </DeleteButton>
                  </DeleteCell>
                </RowWithDelete>
              );
            })}
          </tbody>
        </StyledTable>
        <SmallButtonWrapper>
          <TransparentFlatButton onClick={handleAddRow}>+ Add row</TransparentFlatButton>
        </SmallButtonWrapper>
      </TableWrapper>
    );
  }

  if (typeof data === 'object' && data !== null) {
    const tableWidth = computeTableWidth(visibleColumns, getWidth);
    return (
      <TableWrapper>
        <TableToolbar>
          <ToolbarButton onClick={handleExpandAll} title="Expand all columns">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M18,8H8V18H6V8A2,2 0 0,1 8,6H18V8M14,2H4A2,2 0 0,0 2,4V14H4V4H14V2M22,12V20A2,2 0 0,1 20,22H12A2,2 0 0,1 10,20V12A2,2 0 0,1 12,10H20A2,2 0 0,1 22,12M20,15H17V12H15V15H12V17H15V20H17V17H20V15Z" />
            </svg>
            Expand All
          </ToolbarButton>
          <ToolbarButton onClick={handleCollapseAll} title="Collapse all columns">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M4,2A2,2 0 0,0 2,4V14H4V4H14V2H4M8,6A2,2 0 0,0 6,8V18H8V8H18V6H8M20,12V20H12V12H20M20,10H12A2,2 0 0,0 10,12V20A2,2 0 0,0 12,22H20A2,2 0 0,0 22,20V12A2,2 0 0,0 20,10M19,17H13V15H19V17Z" />
            </svg>
            Collapse All
          </ToolbarButton>
          <HiddenColumnsDropdown
            hiddenColumns={hiddenColumnsList}
            onUnhide={handleUnhideColumn}
            onUnhideAll={handleUnhideAll}
          />
        </TableToolbar>
        <StyledTable style={{ width: `${tableWidth}px`, minWidth: `${tableWidth}px` }}>
          <thead>
            <tr>
              {visibleColumns.map((col) => (
                <ResizableHeader
                  key={col}
                  column={col}
                  width={getWidth(col)}
                  onResize={handleResize}
                  onAutoFit={handleAutoFit}
                  isExpanded={!collapseSignal[col]?.collapsed}
                  onToggleExpand={handleToggleExpand}
                  onHide={handleHideColumn}
                />
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              {visibleColumns.map((col) => {
                const cellValue = data[col];
                const cellPath = [...path, col];
                const cellSignal = collapseSignal[col] || { collapsed: false, version: 0 };
                return <td key={col}>{renderCell(cellValue, cellPath, cellSignal)}</td>;
              })}
            </tr>
          </tbody>
        </StyledTable>
      </TableWrapper>
    );
  }

  return null;
};
